import { z } from "zod";

// Accept `string | null | undefined`: gateway models serialize "no id yet" as
// an explicit JSON `null` (JSON has no `undefined`). Preserving null here is
// safe because mergeModifiedNodeData drops null/undefined keys before merging,
// so a modify payload can't wipe an existing id.
const nullableAssetIdSchema = z.string().nullish();

const assetTypeSchema = z.enum([
  "interface",
  "table",
  "zap",
  "agent",
  "chatbot",
  "3rd_party",
  "text",
]);

const nodeDataSchema = z.object({
  // nullish (null | undefined | string): models serializing "no id yet" emit an
  // explicit JSON `null` (JSON has no `undefined`), so accept both forms.
  projectId: nullableAssetIdSchema.describe(
    "The id of the interface or form project this node references. null if no underlying project exists",
  ),
  pageId: nullableAssetIdSchema.describe(
    "The id of the interface or form page this node references. null if no underlying page exists",
  ),
  tableId: nullableAssetIdSchema.describe(
    "The id of the table this node references. null if no underlying table exists",
  ),
  zapId: nullableAssetIdSchema.describe(
    "The id of the zap this node references. null if no underlying zap exists",
  ),
  agentId: nullableAssetIdSchema.describe(
    "The id of the agent this node references. null if no underlying agent exists",
  ),
  chatbotId: nullableAssetIdSchema.describe(
    "The id of the chatbot this node references. null if no underlying chatbot exists",
  ),
  label: z.string(),
});

const additionSchema = z.object({
  data: nodeDataSchema,
  type: z.enum(["default", "zap", "text"]),
  assetType: assetTypeSchema,
  position: z.object({
    x: z.number(),
    y: z.number(),
  }),
});

const modifySchema = z.object({
  id: z.string().describe("The id of the node to modify"),
  data: nodeDataSchema,
});

export const edgeDiffSchema = z.object({
  add: z.array(z.any()).default([]),
  remove: z.array(z.any()).default([]),
});

export const nodeDiffSchema = z.object({
  add: z.array(additionSchema).default([]),
  remove: z
    .array(z.string().describe("The id of the node to remove"))
    .default([]),
  modify: z.array(modifySchema).default([]),
});

export const parameters = z.object({
  nodeDiff: nodeDiffSchema,
  // Models sometimes omit edgeDiff entirely; default it so a node-only edit
  // doesn't fail on a missing edgeDiff.
  edgeDiff: edgeDiffSchema.default({ add: [], remove: [] }),
  displayMessage: z
    .string()
    .optional()
    .describe(
      "Short 3-5 word friendly message describing what you're doing, like 'Adding new workflow...' or 'Updating canvas layout...'",
    ),
});
